import React, { useEffect, useState } from "react";
import { TextField, Grid, Alert, CircularProgress, Button, Box } from "@mui/material";
import FormContainer from "../../../../../components/Forms/FormContainer";
//import useDecodedUser from "../../../../../services/hooks/useDecodedUser";
import { ExecutePostWithParams } from "../../../../../services/api/ExecuteApiRequests";
import { ParcelsInParkList, InvestorList, ParkList } from "../../../Shared/PreRenderedComponents";
import PlanPrintPreview from "./PlanPrintPreview";
import IPDCToastMessageResult from "../../../../../components/Controls/IPDCToastMessageResult";
import MapDataProvider from "../../GISView/MapRelated/MapData/MapDataProvider";

const initialValue = {
  selectedPark: null,
  selectedParcel: null,
  investor: null,
  newInvestor: "",
  description: "",
  attachment: null,
};

const PreparePlanToIssueToInvestor = () => {
  //const decodedUser = useDecodedUser();
  const [formData, setFormData] = useState(initialValue);
  const [status, setStatus] = useState({ loading: false, error: null, success: null });
  const [openPreview, setOpenPreview] = useState(false);

  const validateForm = () => {
    const { selectedPark, investor, selectedParcel, newInvestor } = formData;
    if (!selectedPark) return "Please select a park";
    if (!(investor || newInvestor)) return "Please select or fill investor";
    if (!selectedParcel) return "Please select a parcel to assign";
    return null;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setStatus({ ...status, error: validationError });
      return;
    }
    setOpenPreview(true);
    setStatus({ loading: true, error: null, success: null });

    try {
      const submitData = new FormData();
      submitData.append("data", JSON.stringify(formData));
      submitData.append("file", formData.attachment);
      const response = await ExecutePostWithParams(`parcels/placeOnHold`, submitData, false);
      if (response?.message) {
        setStatus({ loading: false, error: null, success: response?.message || "Data imported successfully" });
        setFormData(initialValue);
        setOpenPreview(false)
      }
    } catch (error) {
      console.log(error);
    } finally {
      setStatus({ loading: false });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileUpload = event => {
    const file = event.target.files[0];
    handleInputChange("attachment", file);
  };

  const handleOpenPreview = () => {
    if (!(formData.selectedParcel?.upin && formData.selectedPark?.id)) {
      setStatus({ loading: false, error: "Please select a parcel and park", success: null });
      return;
    }
    setOpenPreview(true);
  };

  const handlePrint = async e => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setStatus({ ...status, loading: true, error: null, success: null });
    try {
      const submitData = new FormData();
      submitData.append("upin", formData.selectedParcel?.upin || "");
      submitData.append("investor_id", formData.investor?.id || formData.newInvestor || "");
      submitData.append("description", formData.description || "");

      if (formData.attachment) {
        submitData.append("file", formData.attachment);
      }
      const response = await ExecutePostWithParams(`parcels/${formData.selectedParcel?.upin}/placeOnHold`, submitData);
      if (response?.message) {
        setStatus({error: null, success: response.message, dialogOpen: false });
        setOpenPreview(false);
      }
    } catch (error) {
      console.log(error);
      setStatus({ ...status, error: "An error occurred while submitting the form." });
    } finally {
      setStatus({ ...status, loading: false });
    }
  };

  // Handle Request button click
  const handleRequest = async () => {
    // Validate form before submitting request
    const validationError = validateForm();
    if (validationError) {
      setStatus({ ...status, error: validationError, success: null });
      return;
    }

    setStatus({ loading: true, error: null, success: null });

    try {
      // Prepare request data
      const requestData = {
        upin: formData.selectedParcel?.upin || "",
        park_id: formData.selectedPark?.id || "",
        park_name: formData.selectedPark?.name || "",
        investor_id: formData.investor?.id || null,
        investor_name: formData.investor?.company_name || formData.newInvestor || "",
        description: formData.description || "",
        request_date: new Date().toISOString(),
        status: "Pending",
        attachment_name: formData.attachment?.name || null,
      };

      console.log("Submitting request:", requestData);

      // API call to insert request data into table
      const response = await ExecutePostWithParams(`parcels/requestplan`, requestData,);

      if (response?.message || response?.success) {
        setStatus({
          loading: false,
          error: null,
          success: response?.message || "Request submitted successfully!"
        });
        
        // Reset form after successful request
        setFormData(initialValue);
        
        console.log("Request data inserted successfully:", requestData);
      } else {
        throw new Error(response?.error || "Failed to submit request");
      }
    } catch (error) {
      console.error("Error submitting request:", error);
      setStatus({
        loading: false,
        error: error.message || "An error occurred while submitting the request",
        success: null
      });
    }
  };

  useEffect(() => {
    // Reset selectedParcel when the park changes
    if (formData.selectedPark) {
      handleInputChange("selectedParcel", null);
    }
  }, [formData.selectedPark]);

  return (
    <>
      <MapDataProvider>
        <FormContainer title="Prepare Plan to Issue to Investor" showButton={false}>
          <Grid item xs={6}>
            <InvestorList
              selectedInvestor={formData.investor}
              setSelectedInvestor={investor => handleInputChange("investor", investor)}
              isRequired={false}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="newInvestor"
              value={formData.newInvestor}
              onChange={e => handleInputChange("newInvestor", e.target.value)}
              name="newInvestor"
              label="Fill name for new investor"
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "Georgia, serif",
                  fontSize: "16px",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "Georgia, serif",
                  fontSize: "16px",
                },
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Georgia, serif",
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <ParkList
              selectedPark={formData.selectedPark}
              setSelectedPark={park => handleInputChange("selectedPark", park)}
            />
          </Grid>
          <Grid item xs={6}>
            {formData.selectedPark && (
              <ParcelsInParkList
                key={formData.selectedPark?.id}
                parkId={formData.selectedPark?.id}
                selectedParcel={formData.selectedParcel}
                setSelectedParcel={parcel => handleInputChange("selectedParcel", parcel)}
              />
            )}
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              id="description"
              value={formData.description}
              onChange={e => handleInputChange("description", e.target.value)}
              name="description"
              label="Description"
              multiline
              rows={2}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField 
              type="file" 
              onChange={handleFileUpload} 
              fullWidth 
              InputLabelProps={{ shrink: true }} 
            />
          </Grid>
          
          {status.loading && (
            <Grid item xs={12} justifyContent="center" style={{ marginTop: 16 }}>
              <CircularProgress />
            </Grid>
          )}
          
          <Grid item xs={12} justifyContent="center" style={{ marginTop: 16 }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                type="button" 
                variant="contained" 
                color="primary" 
                onClick={handleOpenPreview}
                size="small"
                disabled={status.loading}
                sx={{ minWidth: '120px' }}
              >
               Preview Plan
              </Button>
              <Button 
                type="button" 
                variant="contained" 
                color="secondary" 
                onClick={handleRequest}
                size="small"
                disabled={status.loading}
                sx={{ minWidth: '120px' }}
              >
                Request
              </Button>
            </Box>
          </Grid>

          {/* Preview Dialog */}
          {formData.selectedParcel && (
            <PlanPrintPreview
              feature={formData.selectedParcel}
              investorName={formData.investor?.company_name || formData.newInvestor || "Company name not stated"}
              open={openPreview}
              onClose={() => setOpenPreview(false)}
              onPrint={handlePrint}
            />
          )}
          
          {(status.error || status.success) && (
            <Grid item xs={12}>
              <Alert severity={status.error ? "warning" : "success"}>
                {status.error || status.success}
              </Alert>
              <IPDCToastMessageResult 
                message={status.error ? status.error : status?.success || ""} 
              />
            </Grid>
          )}
        </FormContainer>
      </MapDataProvider>
    </>
  );
};

export default PreparePlanToIssueToInvestor;